import { Permission } from "@octopusdeploy/octopus-server-client";
import { compact } from "lodash";
import * as React from "react";
import AreaTitle from "~/components/AreaTitle";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import { NavigationSideBarLayoutVNext } from "~/components/NavigationSideBarLayoutVNext/NavigationSideBarLayoutVNext";
import type { NavItem, NavLink } from "~/components/NavigationSidebarLayout";
import NavigationSidebarLayout, { Navigation } from "~/components/NavigationSidebarLayout";
import { PageHeaderPrimary } from "~/components/PageHeaderPrimary";
import type { PaperLayoutProps } from "~/components/PaperLayout";
import PaperLayout from "~/components/PaperLayout";
import { useIsPageHeaderVNextEnabledOutsideOfProjects } from "~/components/RootRoutes/useIsPageHeaderVNextEnabled";
import routeLinks from "~/routeLinks";
import styles from "./style.module.less";
export function InfrastructureLayout({ children }: React.PropsWithChildren<{}>) {
    const dynamicEnvironmentsEnabled = isFeatureToggleEnabled("DynamicEnvironmentsFeatureToggle");
    const isPageHeaderVNextEnabledOutsideOfProjects = useIsPageHeaderVNextEnabledOutsideOfProjects();
    if (isPageHeaderVNextEnabledOutsideOfProjects) {
        return (<main>
                <NavigationSideBarLayoutVNext navItems={getVNextNavItems(dynamicEnvironmentsEnabled)} content={children} header={<PageHeaderPrimary title={"Infrastructure"}/>}/>
            </main>);
    }
    return (<main>
            <AreaTitle link={routeLinks.infrastructure.root} title="Infrastructure"/>
            <NavigationSidebarLayout navLinks={getNavItems(dynamicEnvironmentsEnabled)} content={children}/>
        </main>);
}
function getVNextNavItems(dynamicEnvironmentsEnabled: boolean): NavLink[] {
    const navLinks: Array<NavLink | null> = [];
    navLinks.push(...overviewAndDeploymentTargetLinks());
    if (dynamicEnvironmentsEnabled) {
        navLinks.push(createStaticEnvironmentsNavItem("Environments"));
        navLinks.push(createDynamicEnvironmentsNavItem("Dynamic Environments"));
    }
    else {
        navLinks.push(createStaticEnvironmentsNavItem("Environments"));
    }
    navLinks.push(...extraLinks());
    return compact(navLinks);
}
function getNavItems(dynamicEnvironmentsEnabled: boolean): NavItem[] {
    const navLinks: Array<NavItem | null> = [];
    navLinks.push(...overviewAndDeploymentTargetLinks());
    if (dynamicEnvironmentsEnabled) {
        const environmentsNavItems = compact([createStaticEnvironmentsNavItem(<IndentedNavItemLink label="Static"/>), createDynamicEnvironmentsNavItem(<IndentedNavItemLink label="Dynamic"/>)]);
        const environmentsGroup = Navigation.navGroup("Environments", routeLinks.infrastructure.environments.root, environmentsNavItems, { permission: [Permission.EnvironmentView, Permission.MachineView], wildcard: true }, true);
        navLinks.push(environmentsGroup);
    }
    else {
        navLinks.push(createStaticEnvironmentsNavItem("Environments"));
    }
    navLinks.push(...extraLinks());
    return compact(navLinks);
}
function overviewAndDeploymentTargetLinks(): (NavLink | null)[] {
    return [
        Navigation.navItem("Overview", routeLinks.infrastructure.overview, undefined, { permission: [Permission.EnvironmentView, Permission.MachineView, Permission.WorkerView], wildcard: true }),
        Navigation.navItem("Deployment Targets", routeLinks.infrastructure.machines.root, undefined, { permission: [Permission.MachineView], wildcard: true }),
    ];
}
function extraLinks(): (NavLink | null)[] {
    return [
        Navigation.navItem("Workers", routeLinks.infrastructure.workerMachines.root, undefined, { permission: [Permission.WorkerView], wildcard: true }),
        Navigation.navItem("Worker Pools", routeLinks.infrastructure.workerPools.root, undefined),
        Navigation.navItem("Machine Policies", routeLinks.infrastructure.machinePolicies.root, undefined, { permission: Permission.MachinePolicyView, wildcard: true }),
        Navigation.navItem("Machine Proxies", routeLinks.infrastructure.proxies.root, undefined, { permission: Permission.ProxyView, wildcard: true }),
        Navigation.navItem("Accounts", routeLinks.infrastructure.accounts.root, undefined, { permission: Permission.AccountView, wildcard: true }),
    ];
}
function IndentedNavItemLink(props: {
    label: string | JSX.Element;
}) {
    return <div className={styles.indentedNavItemLinkContainer}>{props.label}</div>;
}
function createStaticEnvironmentsNavItem(label: string | JSX.Element) {
    return Navigation.navItem(label, routeLinks.infrastructure.environments.root, undefined, { permission: [Permission.EnvironmentView, Permission.MachineView], wildcard: true });
}
function createDynamicEnvironmentsNavItem(label: string | JSX.Element) {
    return Navigation.navItem(label, routeLinks.infrastructure.dynamicEnvironments.root(), undefined, { permission: [Permission.EnvironmentView, Permission.MachineView], wildcard: true });
}
export const InfrastructureLayoutBusy: React.FC<PaperLayoutProps> = (props: PaperLayoutProps) => {
    return (<InfrastructureLayout>
            <PaperLayout {...props}/>
        </InfrastructureLayout>);
};
InfrastructureLayoutBusy.displayName = "InfrastructureLayoutBusy"
